import { Action, Reducer } from 'redux';

import { AppThunkAction } from './';
import { User } from '../models/User';
import AuthenticationService, { AzureTokenRequest } from '../services/AuthenticationService';
import { AzureConfigurations } from '../auth-config';
import { StringUtils } from '../components/shared/StringUtils';
import { AzureToken } from '../models/AzureToken';
import { ImpactPartner } from '../models/ImpactPartner';
import ImpactPartnerService from '../services/ImpactPartnerService';

export interface IState {
    currentUser: User;
    isLoading: boolean;
    errorMessage: string;
    allUsers: User[];
    azureToken: AzureToken;
    authImpactPartner: ImpactPartner;
    azureConfig: AzureConfigurations;
}

export enum Actions {
    LoginRequest = "LoginRequest",
    LoginReceive = "LoginReceive",
    LogoutRequest = "LogoutRequest",
    LogoutReceive = "LogoutReceive",
    LoadAllUsersRequest = "LoadAllUsersRequest",
    LoadAllUsersReceive = "LoadAllUsersReceive",
    SetCurrentUser = "SetCurrentUser",
    GetAzureAuthCodeRequest = "GetAzureAuthCodeRequest",
    GetAzureTokenRequest = "GetAzureTokenRequest",
    GetAzureTokenReceive = "GetAzureTokenReceive",
    GetAzureConfigRequest = "GetAzureConfigRequest",
    GetAzureConfigReceive = "GetAzureConfigReceive"
    //Fail = "FAIl",
    //CleanErrorMessage = "CLEAN_ERROR_MESSAGE"
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.

//acquire azure ad b2c authorization code 
interface GetAzureAuthCodeRequestAction {
    type: Actions.GetAzureAuthCodeRequest;
    imactPartner: ImpactPartner;
}

//acquire Token
interface GetAzureTokenRequestAction {
    type: Actions.GetAzureTokenRequest;
}

interface GetAzureTokenReceiveAction {
    type: Actions.GetAzureTokenReceive;
    token: AzureToken;
    user: User;
    errorMessage: string;
}

// login
interface LoginRequestAction {
    type: Actions.LoginRequest;
}

interface LoginReceiveAction {
    type: Actions.LoginReceive;
    currentUser: User;
}


// logout
interface LogoutRequestAction {
    type: Actions.LogoutRequest;
}

interface LogoutRecieveAction {
    type: Actions.LogoutReceive;
}


// LoadAllUsers
interface LoadAllUsersRequestAction {
    type: Actions.LoadAllUsersRequest;
}

interface LoadAllUsersReceiveAction {
    type: Actions.LoadAllUsersReceive;
    users: User[]
}

interface SetCurrentUserAction {
    type: Actions.SetCurrentUser;
    user: User
}

interface GetAzureConfigRequestAction {
    type: Actions.GetAzureConfigRequest;
}

interface GetAzureConfigReceiveAction {
    type: Actions.GetAzureConfigReceive;
    azureConfig: AzureConfigurations
}

const checkLocalStorageAvailability = () => {
    var test = 'test';
    try {
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
    } catch (e) {
        return false;
    }
}
export const saveAzureTokenToLocalStorage = (token: AzureToken) => {
    if (checkLocalStorageAvailability()) {
        localStorage.setItem(`azure_ad_token`, JSON.stringify(token));
    }
}

export const removeFromLocalStorage = (key: string) => {
    if (checkLocalStorageAvailability()) {
        localStorage.removeItem(key);
    }
}

export const getAzureTokenFromLocalStorage = (): AzureToken => {

    if (checkLocalStorageAvailability()) {

        try {

            var value = localStorage.getItem('azure_ad_token');
            var token = JSON.parse(value);

            return token;

        } catch (e) {
            console.log('could not parse token from local storage');
        }
    }

    return undefined;
}

export const getPartnerAzureTokenFromLocalStorage = (partnerName: string): AzureToken => {

    if (checkLocalStorageAvailability()) {

        try {

            var value = localStorage.getItem(`${partnerName}_azure_ad_token`);
            var token = JSON.parse(value);

            return token;

        } catch (e) {
            console.log('could not parse token from local storage');
        }
    }

    return undefined;
}

export const savePartnerAzureTokenToLocalStorage = (partnerName: string, token: AzureToken) => {
    if (checkLocalStorageAvailability()) {
        localStorage.setItem(`${partnerName}_azure_ad_token`, JSON.stringify(token));
    }
}

const saveImpactPartnerToLocalStorage = (partner: ImpactPartner) => {
    if (checkLocalStorageAvailability()) {
        localStorage.setItem(`current_partner`, JSON.stringify(partner));
    }
}

export const getImpactPartnerFromLocalStorage = () => {

    if (checkLocalStorageAvailability()) {

        try {

            var value = localStorage.getItem(`current_partner`);
            var partner = JSON.parse(value);

            return partner;

        } catch (e) {
            console.log('could not parse partner from local storage');
        }
    }

    return {};
}

export const getUserFromLocalStorage = () => {

    if (checkLocalStorageAvailability()) {

        try {

            var value = localStorage.getItem(`current_user`);
            var user = JSON.parse(value);

            return user;

        } catch (e) {
            console.log('could not parse user from local storage');
        }
    }

    return {};
}

const saveUserToLocalStorage = (user: User) => {
    if (checkLocalStorageAvailability()) {
        localStorage.setItem(`current_user`, JSON.stringify(user));
    }
}


// fail
/*interface FailAction {
    type: Actions.Fail;
    message: string;
}

interface CleanErrorMessage {
    type: Actions.CleanErrorMessage;
}*/

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction = /*FailAction | CleanErrorMessage
    |*/ LoginRequestAction | LoginReceiveAction
    | LogoutRequestAction | LogoutRecieveAction
    | LoadAllUsersRequestAction | LoadAllUsersReceiveAction
    | GetAzureTokenRequestAction | GetAzureTokenReceiveAction
    | GetAzureAuthCodeRequestAction | SetCurrentUserAction
    | GetAzureConfigRequestAction | GetAzureConfigReceiveAction;


export const actionCreators = {
    login: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {

    },

    logout: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        dispatch({
            type: Actions.LogoutRequest,
        });

        const state = getState();
        if (state && state.authorization && state.authorization.azureConfig) {
            let authority = `${state.authorization.azureConfig.authority}logout?post_logout_redirect_uri=${state.authorization.azureConfig.logoutRedirectUrl}`;
            let partner = await ImpactPartnerService.get(state.authorization.currentUser.ImapctParnerId);

            if (partner) {
                const url = StringUtils.FormatString(authority, partner.value.TentantName, partner.value.TentantName, partner.value.PolicyName);

                dispatch({
                    type: Actions.LogoutReceive,
                });

                document.location.assign(url);
            }
        }
    },

    getCurrentAzureTokenAndUser: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {

        var token = getAzureTokenFromLocalStorage();
        if (!token) {
            dispatch({ type: Actions.SetCurrentUser, user: undefined });
            /*if (document.location.pathname != "/login" && document.location.pathname != "/auth/") {
                document.location.assign("/login");
            }*/
        }
        else {
            const user = getUserFromLocalStorage();
            dispatch({ type: Actions.SetCurrentUser, user: user });
        }
    },

    getAzureAuthCode: (partner: ImpactPartner): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        dispatch({ type: Actions.GetAzureAuthCodeRequest, imactPartner: partner });

        const state = getState();
        if (state && state.authorization && state.authorization.azureConfig) {

            const scope = `${partner.ClientId} offline_access`;
            let authority = `${state.authorization.azureConfig.authority}authorize?client_id=${partner.ClientId}&nonce=123456789&redirect_uri=${state.authorization.azureConfig.authRedirectUrl}&scope=${scope}&response_type=code&reset_password_policy=${partner.PasswordResetPolicyName}&signup_policy=${partner.PolicyName}`;
            const url = StringUtils.FormatString(authority, partner.TentantName, partner.TentantName, partner.PolicyName);

            document.location.assign(url.toLowerCase());
        }
    },

    redirectToAzurePasswordReset: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {

        const partner = getImpactPartnerFromLocalStorage() as ImpactPartner;

        const state = getState();
        if (state && state.authorization && state.authorization.azureConfig) {

            let authority = `${state.authorization.azureConfig.authority}authorize?client_id=${partner.ClientId}&nonce=123456789&redirect_uri=${state.authorization.azureConfig.authRedirectUrl}&scope=${partner.Scope}&response_type=code`;
            const url = StringUtils.FormatString(authority, partner.TentantName, partner.TentantName, partner.PasswordResetPolicyName);

            document.location.assign(url.toLowerCase());
        }
    },

    getAzureToken: (code: string): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        // Only load data if it's something we don't already have (and are not already loading)

        const partner = getImpactPartnerFromLocalStorage();
        if (partner && partner.Id) {

            const model: AzureTokenRequest = {
                impactPartnerId: partner.Id,
                code: code,
            };

            dispatch({ type: Actions.GetAzureTokenRequest });

            var result = await AuthenticationService.getAzureToken(model);

            if (result.value && result.value.isLogout === true) {
                dispatch({
                    type: Actions.LogoutReceive,
                });

                document.location.assign(result.value.logoutUrl);

                return;
            }

            dispatch({
                type: Actions.GetAzureTokenReceive,
                token: result.value?.token,
                user: result.value?.user,
                errorMessage: !result.hasErrors ? "" : result.errorMessage
            });

            document.location.assign("/");
        }
    },

    loadAllUsers: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        // Only load data if it's something we don't already have (and are not already loading)

        dispatch({ type: Actions.LoadAllUsersRequest });

        var result = await AuthenticationService.loadAllTemp();

        if (!result.hasErrors) {
            dispatch({
                type: Actions.LoadAllUsersReceive,
                users: result.value
            });
        }
    },

    setCurrentUser: (user: User): AppThunkAction<KnownAction> => async (dispatch, getState) => {

        dispatch({
            type: Actions.SetCurrentUser,
            user
        });
    },

    getAzureConfigurations: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {

        dispatch({ type: Actions.GetAzureConfigRequest });

        var result = await AuthenticationService.getAzureConfigurations();

        if (!result.hasErrors) {
            dispatch({
                type: Actions.GetAzureConfigReceive,
                azureConfig: result.value
            });
        }
    }
};

const unloadedState: IState = {    
    currentUser: undefined,
    allUsers: [],
    isLoading: false,
    errorMessage: undefined,
    azureToken: undefined,
    authImpactPartner: undefined,
    azureConfig: undefined
};

export const reducer: Reducer<IState> = (state: IState | undefined, incomingAction: Action): IState => {
    if (state === undefined) {
        return unloadedState;
    }

    const action = incomingAction as KnownAction;
    switch (action.type) {

        case Actions.GetAzureAuthCodeRequest:
            saveImpactPartnerToLocalStorage(action.imactPartner);

            return {
                ...state,
            };

        case Actions.GetAzureTokenRequest:

            return {
                ...state,
                isLoading: true
            };


        case Actions.GetAzureTokenReceive:
            // save to local storage
            saveAzureTokenToLocalStorage(action.token);
            saveUserToLocalStorage(action.user);

            return {
                ...state,
                azureToken: action.token,
                currentUser: action.user,
                isLoading: false,
                errorMessage: action.errorMessage
            };
        // get
        case Actions.LoginRequest:

            return {
                ...state,
                isLoading: true
            };


        case Actions.LoginReceive:

            return {
                ...state,
                currentUser: action.currentUser,
                isLoading: false
            };


        // save
        case Actions.LogoutRequest:
            return {
                ...state,
                isLoading: true
            };

        case Actions.LogoutReceive:
            removeFromLocalStorage(`azure_ad_token`);
            removeFromLocalStorage(`current_user`);
            removeFromLocalStorage('current_partner');

            return {
                ...state,
                currentUser: undefined,
                azureToken: undefined,
                isLoading: false
            };

        case Actions.LoadAllUsersRequest:
            return {
                ...state
            };

        case Actions.LoadAllUsersReceive:
            return {
                ...state,
                allUsers: action.users,
                //currentUser: action.users.find(x => x.ImapctParnerId == 'c10aa48125804211a3cdc0b7e23f2797')
            };

        case Actions.SetCurrentUser:
            return {
                ...state,
                currentUser: action.user
            };

        case Actions.GetAzureConfigRequest:
            return {
                ...state
            };

        case Actions.GetAzureConfigReceive:
            return {
                ...state,
                azureConfig: action.azureConfig
            };
    }

    return { ...state };
};